// Roster Management Utilities

import {
  USE_ROSTER_FIXTURES,
  getFixtureRoster,
  saveFixtureRoster,
  resolveVariationKey,
} from '../services/rosterFixtures';

// Position definitions
export const POSITIONS = {
  C: 'Catcher',
  '1B': 'First Baseman',
  '2B': 'Second Baseman',
  '3B': 'Third Baseman',
  SS: 'Shortstop',
  MI: 'Middle Infielder', // 2B or SS
  CI: 'Corner Infielder', // 1B or 3B
  OF: 'Outfielder',
  UTIL: 'Utility', // Any hitter
  P: 'Pitcher',
  SP: 'Starting Pitcher',
  RP: 'Relief Pitcher'
};

// Active lineup requirements
export const ACTIVE_LINEUP_REQUIREMENTS = {
  C: 1,      // 1 Catcher
  '1B': 1,   // 1 First Baseman
  '2B': 1,   // 1 Second Baseman
  '3B': 1,   // 1 Third Baseman
  SS: 1,     // 1 Shortstop
  MI: 1,     // 1 Middle Infielder (2B or SS)
  CI: 1,     // 1 Corner Infielder (1B or 3B)
  OF: 4,     // 4 Outfielders
  UTIL: 2,   // 2 Utility (any hitter)
  P: 9       // 9 Pitchers
};

// Maximum roster size
export const MAX_ROSTER_SIZE = 30;

// Maximum minor league roster size
export const MAX_MINOR_LEAGUE_SIZE = 5;

// Multi-slot positions: stored as arrays, sorted alphabetically
const PITCHER_SLOTS = 9;
const OF_SLOTS = 4;
const UTIL_SLOTS = 2;

const sortByName = (arr) => [...(arr || [])].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
const sortPitchersByName = sortByName;

// Get outfielders from lineup (handles OF array and legacy OF1-OF4)
export const getOutfielders = (activeLineup) => {
  if (!activeLineup) return [];
  if (Array.isArray(activeLineup.OF)) return sortByName(activeLineup.OF);
  const legacy = [];
  for (let i = 1; i <= OF_SLOTS; i++) {
    const p = activeLineup[`OF${i}`];
    if (p) legacy.push(p);
  }
  return sortByName(legacy);
};

// Get utility players from lineup (handles UTIL array and legacy UTIL1-UTIL2)
export const getUtility = (activeLineup) => {
  if (!activeLineup) return [];
  if (Array.isArray(activeLineup.UTIL)) return sortByName(activeLineup.UTIL);
  const legacy = [];
  for (let i = 1; i <= UTIL_SLOTS; i++) {
    const p = activeLineup[`UTIL${i}`];
    if (p) legacy.push(p);
  }
  return sortByName(legacy);
};

// Get pitchers from lineup (handles both P array and legacy P1-P9)
export const getPitchers = (activeLineup) => {
  if (!activeLineup) return [];
  if (Array.isArray(activeLineup.P)) return sortPitchersByName(activeLineup.P);
  const legacy = [];
  for (let i = 1; i <= PITCHER_SLOTS; i++) {
    const p = activeLineup[`P${i}`];
    if (p) legacy.push(p);
  }
  return sortPitchersByName(legacy);
};

// Migrate legacy OF1-OF4 to OF array
const migrateOutfieldersToOF = (activeLineup) => {
  if (!activeLineup) return activeLineup;
  if (Array.isArray(activeLineup.OF)) return activeLineup;
  const of = getOutfielders(activeLineup);
  const next = { ...activeLineup };
  for (let i = 1; i <= OF_SLOTS; i++) delete next[`OF${i}`];
  next.OF = of;
  return next;
};

// Migrate legacy UTIL1-UTIL2 to UTIL array
const migrateUtilityToUTIL = (activeLineup) => {
  if (!activeLineup) return activeLineup;
  if (Array.isArray(activeLineup.UTIL)) return activeLineup;
  const util = getUtility(activeLineup);
  const next = { ...activeLineup };
  for (let i = 1; i <= UTIL_SLOTS; i++) delete next[`UTIL${i}`];
  next.UTIL = util;
  return next;
};

// Migrate legacy P1-P9 to P array
export const migratePitchersToP = (activeLineup) => {
  if (!activeLineup) return activeLineup;
  if (Array.isArray(activeLineup.P)) return activeLineup;
  const pitchers = getPitchers(activeLineup);
  const next = { ...activeLineup };
  for (let i = 1; i <= PITCHER_SLOTS; i++) delete next[`P${i}`];
  next.P = pitchers;
  return next;
};

// Full migration of all multi-slot positions
const migrateActiveLineup = (activeLineup) => {
  if (!activeLineup) return activeLineup;
  let next = migrateOutfieldersToOF(activeLineup);
  next = migrateUtilityToUTIL(next);
  next = migratePitchersToP(next);
  return next;
};

// Get player at lineup position (handles P array)
export const getPlayerAtPosition = (activeLineup, pos) => {
  if (!activeLineup) return null;
  if (pos === 'P') return null; // P is array, use getPitchers
  return activeLineup[pos] ?? null;
};

// Get current date in YYYY-MM-DD format
export const getCurrentDate = () => {
  return new Date().toISOString().split('T')[0];
};

// Get date key for storage
export const getDateKey = (date = getCurrentDate()) => {
  return `roster_${date}`;
};

// Get the Monday of the current week for a given date
export const getWeekStart = (date = getCurrentDate()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  const monday = new Date(d.setDate(diff));
  return monday.toISOString().split('T')[0];
};

// Get the Sunday of the current week for a given date
export const getWeekEnd = (date = getCurrentDate()) => {
  const weekStart = new Date(getWeekStart(date));
  const sunday = new Date(weekStart);
  sunday.setDate(weekStart.getDate() + 6);
  return sunday.toISOString().split('T')[0];
};

// Check if roster is locked based on roster period setting
export const isRosterLocked = (rosterPeriod, currentDate = getCurrentDate()) => {
  if (!rosterPeriod || rosterPeriod === 'daily') {
    return { locked: false, reason: null };
  }

  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const currentHour = now.getHours();
  
  // For weekly or weekly-daily-lineup, rosters lock when the first game of the week starts
  // Typically MLB games start around 1 PM ET on Monday
  if (rosterPeriod === 'weekly' || rosterPeriod === 'weekly-daily-lineup') {
    const weekStart = getWeekStart(currentDate);
    const weekEnd = getWeekEnd(currentDate);
    
    // If it's Monday and after 1 PM, the week is locked
    // If it's Tuesday-Sunday, the week is locked
    const isAfterMonday1PM = currentDay === 1 && currentHour >= 13;
    const isTuesdayToSunday = currentDay >= 2 || currentDay === 0;
    
    if (isAfterMonday1PM || isTuesdayToSunday) {
      const lockType = rosterPeriod === 'weekly' ? 'roster and lineup' : 'roster adds/drops';
      return {
        locked: true,
        lockType: rosterPeriod === 'weekly' ? 'full' : 'roster-only',
        reason: `Weekly ${lockType} locked until Monday. Current week: ${weekStart} to ${weekEnd}`,
        weekStart,
        weekEnd
      };
    }
  }
  
  return { locked: false, reason: null };
};

// Check if position is eligible for a lineup spot
export const isPositionEligible = (playerPosition, lineupPosition) => {
  const primaryPosition = playerPosition.split('/')[0]; // Use the first position if multiple
  
  // Handle specific lineup positions
  switch (lineupPosition) {
    case 'C':
      return primaryPosition === 'C';
    case '1B':
      return primaryPosition === '1B';
    case '2B':
      return primaryPosition === '2B';
    case '3B':
      return primaryPosition === '3B';
    case 'SS':
      return primaryPosition === 'SS';
    case 'MI':
      return ['2B', 'SS'].includes(primaryPosition);
    case 'CI':
      return ['1B', '3B'].includes(primaryPosition);
    case 'OF':
      return ['LF', 'CF', 'RF', 'OF'].includes(primaryPosition);
    case 'UTIL':
      // Any hitter can play utility
      return !['SP', 'RP', 'P'].includes(primaryPosition);
    case 'P':
      return ['SP', 'RP', 'P'].includes(primaryPosition);
    default:
      return false;
  }
};

// Validate active lineup
export const validateActiveLineup = (activeLineup) => {
  const errors = [];
  const counts = { ...ACTIVE_LINEUP_REQUIREMENTS };
  Object.keys(counts).forEach(k => { counts[k] = 0; });

  counts.OF = getOutfielders(activeLineup).length;
  counts.UTIL = getUtility(activeLineup).length;
  counts.P = getPitchers(activeLineup).length;

  const singlePositions = ['C', '1B', '2B', '3B', 'SS', 'MI', 'CI'];
  singlePositions.forEach(pos => {
    const p = activeLineup?.[pos];
    if (p && !Array.isArray(p)) counts[pos]++;
  });
  
  // Check requirements
  Object.entries(ACTIVE_LINEUP_REQUIREMENTS).forEach(([pos, required]) => {
    const have = counts[pos] ?? 0;
    if (have < required) {
      errors.push(`Need ${required} ${POSITIONS[pos]}, have ${have}`);
    } else if (have > required) {
      errors.push(`Too many ${POSITIONS[pos]}, have ${have}, need ${required}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Create empty roster structure
export const createEmptyRoster = () => {
  return {
    players: [],
    activeLineup: {
      C: null,
      '1B': null,
      '2B': null,
      '3B': null,
      SS: null,
      MI: null,
      CI: null,
      OF: [],
      UTIL: [],
      P: []
    },
    bench: [],
    minorLeague: [],
    lastUpdated: getCurrentDate()
  };
};

// Add player to roster
export const addPlayerToRoster = (roster, player) => {
  if (roster.players.length >= MAX_ROSTER_SIZE) {
    return {
      success: false,
      error: `Roster is full. Maximum ${MAX_ROSTER_SIZE} players allowed.`
    };
  }
  
  // Check if player already exists
  const existingPlayer = roster.players.find(p => p.id === player.id);
  if (existingPlayer) {
    return {
      success: false,
      error: 'Player is already on your roster.'
    };
  }
  
  const newRoster = {
    ...roster,
    players: [...roster.players, player],
    bench: [...roster.bench, player], // Add to bench by default
    lastUpdated: getCurrentDate()
  };
  
  return {
    success: true,
    roster: newRoster
  };
};

// Remove player from roster
export const removePlayerFromRoster = (roster, playerId) => {
  const playerIndex = roster.players.findIndex(p => p.id === playerId);
  if (playerIndex === -1) {
    return {
      success: false,
      error: 'Player not found on roster.'
    };
  }
  
  const newRoster = {
    ...roster,
    players: roster.players.filter(p => p.id !== playerId),
    lastUpdated: getCurrentDate()
  };

  // Remove from active lineup (handle P, OF, UTIL arrays and single-slot positions)
  newRoster.activeLineup = migrateActiveLineup(newRoster.activeLineup || {});
  const multiSlot = ['P', 'OF', 'UTIL'];
  multiSlot.forEach(pos => {
    if (Array.isArray(newRoster.activeLineup[pos])) {
      newRoster.activeLineup[pos] = sortByName((newRoster.activeLineup[pos] || []).filter(p => p?.id !== playerId));
    }
  });
  ['C', '1B', '2B', '3B', 'SS', 'MI', 'CI'].forEach(pos => {
    if (newRoster.activeLineup[pos]?.id === playerId) newRoster.activeLineup[pos] = null;
  });
  // Legacy OF1-4, UTIL1-2, P1-9
  Object.keys(newRoster.activeLineup || {}).filter(k => /^(OF|UTIL|P)\d+$/.test(k)).forEach(pos => {
    if (newRoster.activeLineup[pos]?.id === playerId) newRoster.activeLineup[pos] = null;
  });

  newRoster.bench = (newRoster.bench || []).filter(p => p.id !== playerId);
  
  return {
    success: true,
    roster: newRoster
  };
};

// Add player to active lineup
export const addPlayerToLineup = (roster, playerId, position) => {
  const player = roster.players.find(p => p.id === playerId);
  if (!player) {
    return {
      success: false,
      error: 'Player not found on roster.'
    };
  }
  
  if (!isPositionEligible(player.position, position)) {
    return {
      success: false,
      error: `${player.name} is not eligible for ${POSITIONS[position] || position} position.`
    };
  }

  const newActiveLineup = migrateActiveLineup({ ...roster.activeLineup });
  if (position === 'P') {
    const pitchers = getPitchers(newActiveLineup).filter(p => p.id !== playerId);
    pitchers.push(player);
    newActiveLineup.P = sortByName(pitchers);
  } else if (position === 'OF') {
    const of = getOutfielders(newActiveLineup).filter(p => p.id !== playerId);
    of.push(player);
    newActiveLineup.OF = sortByName(of);
  } else if (position === 'UTIL') {
    const util = getUtility(newActiveLineup).filter(p => p.id !== playerId);
    util.push(player);
    newActiveLineup.UTIL = sortByName(util);
  } else {
    newActiveLineup[position] = player;
  }

  const newRoster = {
    ...roster,
    activeLineup: newActiveLineup,
    lastUpdated: getCurrentDate()
  };

  newRoster.bench = (newRoster.bench || []).filter(p => p.id !== playerId);
  
  return {
    success: true,
    roster: newRoster
  };
};

// Remove player from active lineup (for P/OF/UTIL, pass playerId as third arg)
export const removePlayerFromLineup = (roster, position, playerId = null) => {
  let player;
  if (position === 'P') {
    const arr = getPitchers(roster.activeLineup || {});
    player = playerId ? arr.find(p => p?.id === playerId) : arr[0];
  } else if (position === 'OF') {
    const arr = getOutfielders(roster.activeLineup || {});
    player = playerId ? arr.find(p => p?.id === playerId) : arr[0];
  } else if (position === 'UTIL') {
    const arr = getUtility(roster.activeLineup || {});
    player = playerId ? arr.find(p => p?.id === playerId) : arr[0];
  } else {
    player = roster.activeLineup[position];
  }
  if (!player) return { success: false, error: 'No player in this lineup position.' };

  const base = migrateActiveLineup({ ...roster.activeLineup });
  const newActiveLineup = { ...base };
  if (position === 'P') {
    newActiveLineup.P = sortByName((base.P || []).filter(p => p?.id !== player.id));
  } else if (position === 'OF') {
    newActiveLineup.OF = sortByName((base.OF || []).filter(p => p?.id !== player.id));
  } else if (position === 'UTIL') {
    newActiveLineup.UTIL = sortByName((base.UTIL || []).filter(p => p?.id !== player.id));
  } else {
    newActiveLineup[position] = null;
  }

  const newRoster = {
    ...roster,
    activeLineup: newActiveLineup,
    bench: [...(roster.bench || []), player],
    lastUpdated: getCurrentDate()
  };

  return { success: true, roster: newRoster };
};

// Move player to bench
export const movePlayerToBench = (roster, playerId) => {
  const player = roster.players.find(p => p.id === playerId);
  if (!player) {
    return {
      success: false,
      error: 'Player not found on roster.'
    };
  }
  
  const newRoster = {
    ...roster,
    bench: [...(roster.bench || []).filter(p => p.id !== playerId), player],
    minorLeague: (roster.minorLeague || []).filter(p => p.id !== playerId),
    injuredList: (roster.injuredList || []).filter(p => p.id !== playerId),
    lastUpdated: getCurrentDate()
  };

  newRoster.activeLineup = migrateActiveLineup(newRoster.activeLineup || {});
  ['P', 'OF', 'UTIL'].forEach(pos => {
    if (Array.isArray(newRoster.activeLineup[pos])) {
      newRoster.activeLineup[pos] = sortByName((newRoster.activeLineup[pos] || []).filter(p => p?.id !== playerId));
    }
  });
  ['C', '1B', '2B', '3B', 'SS', 'MI', 'CI'].forEach(pos => {
    if (newRoster.activeLineup[pos]?.id === playerId) newRoster.activeLineup[pos] = null;
  });
  Object.keys(newRoster.activeLineup || {}).filter(k => /^(OF|UTIL|P)\d+$/.test(k)).forEach(pos => {
    if (newRoster.activeLineup[pos]?.id === playerId) newRoster.activeLineup[pos] = null;
  });

  return { success: true, roster: newRoster };
};

// Normalize roster from API/localStorage (ensure bench, minorLeague, activeLineup structure)
const normalizeRoster = (r) => {
  if (!r) return createEmptyRoster();
  let activeLineup = r.activeLineup ?? createEmptyRoster().activeLineup;
  activeLineup = migrateActiveLineup(activeLineup);
  const roster = {
    players: r.players ?? [],
    bench: r.bench ?? [],
    minorLeague: r.minorLeague ?? [],
    activeLineup,
    lineupsByDate: r.lineupsByDate ?? null,
    lastUpdated: r.lastUpdated || getCurrentDate()
  };
  if (!roster.bench) roster.bench = [];
  if (!roster.minorLeague) roster.minorLeague = [];
  const activeIds = new Set();
  ['P', 'OF', 'UTIL'].forEach(pos => {
    (roster.activeLineup?.[pos] || []).forEach(p => p?.id && activeIds.add(p.id));
  });
  ['C', '1B', '2B', '3B', 'SS', 'MI', 'CI'].forEach(pos => {
    const p = roster.activeLineup?.[pos];
    if (p && !Array.isArray(p) && p.id) activeIds.add(p.id);
  });
  const minorIds = new Set((roster.minorLeague || []).map((p) => p.id));
  const notInLineup = (roster.players || []).filter(
    (p) => !activeIds.has(p.id) && !minorIds.has(p.id)
  );
  const benchIds = new Set((roster.bench || []).map((p) => p.id));
  notInLineup.forEach((p) => {
    if (!benchIds.has(p.id)) {
      roster.bench.push(p);
      benchIds.add(p.id);
    }
  });
  return roster;
};

// localStorage-backed get (sync)
const getRosterLocal = (userId, leagueId, date = getCurrentDate()) => {
  const dateKey = getDateKey(date);
  const storageKey = `roster_${userId}_${leagueId}_${dateKey}`;
  const saved = localStorage.getItem(storageKey);
  if (!saved) return createEmptyRoster();
  return normalizeRoster(JSON.parse(saved));
};

// localStorage-backed save (sync)
const saveRosterLocal = (userId, leagueId, roster, date = getCurrentDate()) => {
  const dateKey = getDateKey(date);
  const storageKey = `roster_${userId}_${leagueId}_${dateKey}`;
  localStorage.setItem(storageKey, JSON.stringify(roster));
};

/**
 * Get roster for a date. Uses backend when league (with teams) is provided; otherwise localStorage.
 * @param {string} userId - owner userId
 * @param {string} leagueId
 * @param {string} date - YYYY-MM-DD
 * @param {object} [league] - league with teams, settings.rosterSettings.rosterPeriod
 * @param {string} [userTeamId] - optional team id (id or teamId) for current user; avoids lookup when provided
 * @returns {Promise<object>} roster
 */
const normalizeTeams = (league) => {
  const t = league?.teams;
  if (Array.isArray(t)) return t;
  if (typeof t === 'string') try { return JSON.parse(t); } catch { return []; }
  return [];
};

function resolveTeamId(league, userId, userTeamId) {
  if (userTeamId) return userTeamId;
  const teams = normalizeTeams(league);
  const myTeam = teams.find((t) => t.owner === userId || (t.id && String(t.id) === String(userId)) || (t.teamId && String(t.teamId) === String(userId)));
  return myTeam?.id ?? myTeam?.teamId ?? null;
}

// Stable per-team key used for fixture caching + variation selection.
const fixtureTeamKey = (league, userId, userTeamId) =>
  resolveTeamId(league, userId, userTeamId) || userId || 'default';

// Exposed so UI (e.g. the roster fixture-variation switcher) can target the
// exact same team key the roster loader uses.
export const getRosterTeamKey = (league, userId, userTeamId = null) =>
  fixtureTeamKey(league, userId, userTeamId);

export const getRosterForDate = async (userId, leagueId, date = getCurrentDate(), league = null, userTeamId = null) => {
  // Local-fixtures mode: no backend call, return a variation of sample data.
  if (USE_ROSTER_FIXTURES) {
    const teamId = fixtureTeamKey(league, userId, userTeamId);
    const variation = resolveVariationKey(teamId);
    const cacheKey = `${leagueId}:${teamId}:${date}`;
    const res = getFixtureRoster(cacheKey, variation);
    return normalizeRoster(res.roster);
  }
  const teams = normalizeTeams(league);
  if (!teams.length) return Promise.resolve(getRosterLocal(userId, leagueId, date));
  try {
    const rosterService = (await import('../services/rosterService')).default;
    const periodType = league?.settings?.rosterSettings?.rosterPeriod || 'daily';
    const periodKey = rosterService.resolvePeriodKey(date, periodType, getWeekStart);
    const teamId = resolveTeamId(league, userId, userTeamId);
    const res = await rosterService.getRoster(userId, leagueId, teamId, periodKey, periodType);
    if (!res.success) return Promise.resolve(getRosterLocal(userId, leagueId, date));
    if (!res.roster) return Promise.resolve(getRosterLocal(userId, leagueId, date));
    return normalizeRoster(res.roster);
  } catch {
    return Promise.resolve(getRosterLocal(userId, leagueId, date));
  }
};

// Get all players currently on rosters for a league (localStorage only; backend use list by league)
export const getRosteredPlayers = (leagueId) => {
  const rosteredPlayerIds = new Set();
  const keys = Object.keys(localStorage);
  const re = new RegExp(`roster_.+_${String(leagueId).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_roster_(\\d{4}-\\d{2}-\\d{2})`);
  keys.forEach((key) => {
    const match = key.match(re);
    if (match) {
      try {
        const roster = JSON.parse(localStorage.getItem(key));
        if (roster?.players) roster.players.forEach((p) => p?.id && rosteredPlayerIds.add(p.id));
      } catch (_) {}
    }
  });
  return Array.from(rosteredPlayerIds);
};

/**
 * Save roster for a date. Uses backend when league provided; else localStorage.
 * @param {string} [userTeamId] - optional team id for current user; when provided avoids lookup and ensures backend save uses valid teamId
 */
export const saveRosterForDate = async (userId, leagueId, roster, date = getCurrentDate(), league = null, userTeamId = null) => {
  // Local-fixtures mode: persist to the in-memory session cache only (no backend).
  if (USE_ROSTER_FIXTURES) {
    const teamId = fixtureTeamKey(league, userId, userTeamId);
    saveFixtureRoster(`${leagueId}:${teamId}:${date}`, roster);
    return;
  }
  const teams = normalizeTeams(league);
  if (!teams.length) {
    saveRosterLocal(userId, leagueId, roster, date);
    return;
  }
  const teamId = resolveTeamId(league, userId, userTeamId);
  if (!teamId) {
    saveRosterLocal(userId, leagueId, roster, date);
    return;
  }
  try {
    const rosterService = (await import('../services/rosterService')).default;
    const periodType = league?.settings?.rosterSettings?.rosterPeriod || 'daily';
    const periodKey = rosterService.resolvePeriodKey(date, periodType, getWeekStart);
    await rosterService.saveRoster(userId, leagueId, teamId, periodKey, periodType, roster);
  } catch (e) {
    console.error('saveRosterForDate: backend save failed, falling back to localStorage', e);
    saveRosterLocal(userId, leagueId, roster, date);
    throw e;
  }
};

/**
 * Get available period keys (dates or week starts). Uses backend when league provided.
 */
export const getAvailableDates = async (userId, leagueId, league = null) => {
  const teams = normalizeTeams(league);
  if (!teams.length) {
    const dates = [];
    const keys = Object.keys(localStorage);
    const re = new RegExp(`roster_${String(userId).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_${String(leagueId).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_roster_(\\d{4}-\\d{2}-\\d{2})`);
    keys.forEach((key) => {
      const m = key.match(re);
      if (m) dates.push(m[1]);
    });
    return dates.sort();
  }
  try {
    const rosterService = (await import('../services/rosterService')).default;
    const teamId = resolveTeamId(league, userId, null);
    if (!teamId) return [];
    const res = await rosterService.listPeriodKeys(leagueId, teamId);
    return res.success ? (res.periodKeys || []).sort() : [];
  } catch {
    return [];
  }
};

/**
 * Update lineup for all future period keys. Uses backend when league provided.
 */
export const updateLineupForAllFutureDates = async (userId, leagueId, activeLineup, currentDate = getCurrentDate(), league = null) => {
  try {
    const allKeys = await getAvailableDates(userId, leagueId, league);
    const futureKeys = allKeys.filter((k) => k > currentDate);
    for (const key of futureKeys) {
      try {
        const roster = await getRosterForDate(userId, leagueId, key, league);
        const updated = { ...roster, activeLineup: { ...activeLineup }, lastUpdated: getCurrentDate() };
        await saveRosterForDate(userId, leagueId, updated, key, league);
      } catch (error) {
        console.warn(`Failed to update roster for date ${key}:`, error);
        // Continue with other dates even if one fails
      }
    }
    return futureKeys.length;
  } catch (error) {
    console.error('Error in updateLineupForAllFutureDates:', error);
    throw error;
  }
};

/**
 * Copy roster from one date to another.
 */
export const copyRosterToDate = async (userId, leagueId, fromDate, toDate, league = null) => {
  const fromRoster = await getRosterForDate(userId, leagueId, fromDate, league);
  const newRoster = { ...fromRoster, lastUpdated: toDate };
  await saveRosterForDate(userId, leagueId, newRoster, toDate, league);
  return newRoster;
};

// Get eligible lineup positions for a player
export const getEligibleLineupPositions = (playerPosition) => {
  const eligiblePositions = [];
  const primaryPosition = playerPosition.split('/')[0];
  
  // Check each specific lineup position
  const lineupPositions = ['C', '1B', '2B', '3B', 'SS', 'MI', 'CI', 'OF', 'UTIL', 'P'];
  
  lineupPositions.forEach(pos => {
    if (isPositionEligible(playerPosition, pos)) {
      eligiblePositions.push(pos);
    }
  });
  
  return eligiblePositions;
};

// Move player to specific lineup position (async when userId/leagueId/currentDate provided)
export const movePlayerToLineupPosition = async (
  roster,
  playerId,
  lineupPosition,
  userId = null,
  leagueId = null,
  currentDate = null,
  league = null
) => {
  const player = roster.players.find(p => p.id === playerId);
  if (!player) {
    return {
      success: false,
      error: 'Player not found on roster.'
    };
  }
  
  // Check if position is eligible
  const eligiblePositions = getEligibleLineupPositions(player.position);
  if (!eligiblePositions.includes(lineupPosition)) {
    return {
      success: false,
      error: `${player.name} is not eligible for this lineup position.`
    };
  }
  
  const base = migrateActiveLineup({ ...roster.activeLineup });
  const newActiveLineup = { ...base };

  ['P', 'OF', 'UTIL'].forEach(pos => {
    if (Array.isArray(base[pos])) {
      newActiveLineup[pos] = (base[pos] || []).filter(p => p?.id !== playerId);
    }
  });
  ['C', '1B', '2B', '3B', 'SS', 'MI', 'CI'].forEach(pos => {
    if (newActiveLineup[pos]?.id === playerId) newActiveLineup[pos] = null;
  });
  Object.keys(newActiveLineup).filter(k => /^(OF|UTIL|P)\d+$/.test(k)).forEach(pos => {
    if (newActiveLineup[pos]?.id === playerId) delete newActiveLineup[pos];
  });

  if (lineupPosition === 'P') {
    const pitchers = [...(newActiveLineup.P || []), player];
    newActiveLineup.P = sortByName(pitchers);
  } else if (lineupPosition === 'OF') {
    const of = [...(newActiveLineup.OF || []), player];
    newActiveLineup.OF = sortByName(of);
  } else if (lineupPosition === 'UTIL') {
    const util = [...(newActiveLineup.UTIL || []), player];
    newActiveLineup.UTIL = sortByName(util);
  } else {
    newActiveLineup[lineupPosition] = player;
  }

  const newRoster = {
    ...roster,
    activeLineup: newActiveLineup,
    bench: (roster.bench || []).filter(p => p.id !== playerId),
    minorLeague: (roster.minorLeague || []).filter(p => p.id !== playerId),
    injuredList: (roster.injuredList || []).filter(p => p.id !== playerId),
    lastUpdated: getCurrentDate()
  };
  
  // Update future dates if userId, leagueId, currentDate (and optionally league) are provided
  if (userId && leagueId && currentDate) {
    try {
      const futureDatesUpdated = await updateLineupForAllFutureDates(
        userId,
        leagueId,
        newRoster.activeLineup,
        currentDate,
        league ?? null
      );
      return {
        success: true,
        roster: newRoster,
        futureDatesUpdated
      };
    } catch (error) {
      console.error('Error updating future dates:', error);
      // Still return success for the current roster update, but log the error
      return {
        success: true,
        roster: newRoster,
        futureDatesUpdated: 0,
        warning: 'Failed to update future dates: ' + (error.message || 'Unknown error')
      };
    }
  }

  return {
    success: true,
    roster: newRoster
  };
};

// Move player to minor league
export const movePlayerToMinorLeague = (roster, playerId) => {
  const player = roster.players.find(p => p.id === playerId);
  if (!player) {
    return {
      success: false,
      error: 'Player not found on roster.'
    };
  }
  
  // Check if minor league is full
  if (!roster.minorLeague) {
    roster.minorLeague = [];
  }
  
  if (roster.minorLeague.length >= MAX_MINOR_LEAGUE_SIZE) {
    return {
      success: false,
      error: `Minor league is full. Maximum ${MAX_MINOR_LEAGUE_SIZE} players allowed.`
    };
  }
  
  const newRoster = {
    ...roster,
    minorLeague: [...(roster.minorLeague || []), player],
    bench: (roster.bench || []).filter(p => p.id !== playerId),
    lastUpdated: getCurrentDate()
  };

  newRoster.activeLineup = migrateActiveLineup(newRoster.activeLineup || {});
  ['P', 'OF', 'UTIL'].forEach(pos => {
    if (Array.isArray(newRoster.activeLineup[pos])) {
      newRoster.activeLineup[pos] = sortByName((newRoster.activeLineup[pos] || []).filter(p => p?.id !== playerId));
    }
  });
  ['C', '1B', '2B', '3B', 'SS', 'MI', 'CI'].forEach(pos => {
    if (newRoster.activeLineup[pos]?.id === playerId) newRoster.activeLineup[pos] = null;
  });
  Object.keys(newRoster.activeLineup || {}).filter(k => /^(OF|UTIL|P)\d+$/.test(k)).forEach(pos => {
    if (newRoster.activeLineup[pos]?.id === playerId) newRoster.activeLineup[pos] = null;
  });

  return { success: true, roster: newRoster };
};

// Move player from minor league to bench
export const movePlayerFromMinorLeague = (roster, playerId) => {
  if (!roster.minorLeague) {
    roster.minorLeague = [];
  }
  
  const player = roster.minorLeague.find(p => p.id === playerId);
  if (!player) {
    return {
      success: false,
      error: 'Player not found in minor league.'
    };
  }
  
  const newRoster = {
    ...roster,
    minorLeague: roster.minorLeague.filter(p => p.id !== playerId),
    bench: [...roster.bench, player],
    lastUpdated: getCurrentDate()
  };
  
  return {
    success: true,
    roster: newRoster
  };
};

// Get roster summary
export const getRosterSummary = (roster) => {
  const pitchers = getPitchers(roster?.activeLineup || {});
  const of = getOutfielders(roster?.activeLineup || {});
  const util = getUtility(roster?.activeLineup || {});
  const singleCount = ['C', '1B', '2B', '3B', 'SS', 'MI', 'CI'].filter(pos => roster?.activeLineup?.[pos]).length;
  const activeCount = singleCount + pitchers.length + of.length + util.length;
  const benchCount = roster.bench.length;
  const minorLeagueCount = (roster.minorLeague || []).length;
  const totalCount = roster.players.length;
  
  return {
    active: activeCount,
    bench: benchCount,
    minorLeague: minorLeagueCount,
    total: totalCount,
    available: MAX_ROSTER_SIZE - totalCount,
    minorLeagueAvailable: MAX_MINOR_LEAGUE_SIZE - minorLeagueCount,
    validation: validateActiveLineup(roster.activeLineup)
  };
};
